<template>
  <BaseModal ref="modalRef" class="max-w-[500px]!" @close="resetForm">
    <form action="" @submit.prevent="loginUser">
      <div class="">
        <div class="mb-3 flex flex-col gap-y-2 items-center">
          <h2 class="text-2xl text-center font-semibold text-white">Welcome back</h2>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <div class="input-group">
            <div class="input-label">Username</div>
            <input v-model="username" type="text" class="input-item w-full" />

            <template v-if="$v.username.$dirty">
              <span v-for="error in $v.username.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
          <BasePasswordInput v-model="password" label="Password">
            <template #errors>
              <template v-if="$v.password.$dirty">
                <span v-for="error in $v.password.$errors" :key="error.$uid" class="input-error">
                  {{ error.$message }}
                </span>
              </template>
            </template>
          </BasePasswordInput>
          <p v-if="errorMessage" class="text-cw-red">{{ errorMessage }}</p>
        </div>
        <div class="w-max mt-4">
          <button type="button" class="text-cw-green hover:underline" @click="openResetPassword">Forgot password?</button>
        </div>
        <div class="w-max ml-auto mt-4">
          <BaseButton background-color="var(--color-cw-lilac)" border-color="#1B0623" class="px-10 py-3.5 mt-8" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Login</p>
          </BaseButton>
        </div>
      </div>
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import { useTemplateRef, ref, nextTick } from 'vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BasePasswordInput from '@/components/base/BasePasswordInput.vue'
import Loader from '@/components/interface/Loader.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useRouter } from 'vue-router'
import { useHelpers } from '@/composables/useHelpers'
import AuthService from '@/services/api/auth-service'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { useUserStore } from '@/stores/user'

const { push } = useRouter()
const { setCookie, sleep } = useHelpers()
const { setUser } = useUserStore()

const modalRef = useTemplateRef('modalRef')
const emit = defineEmits(['open-reset-password'])
const username = ref('')
const password = ref('')
const errorMessage = ref('')
const isLoading = ref(false)

const $v = useVuelidate(
  {
    username: {
      required: helpers.withMessage('Enter your username', required),
      $autoDirty: true
    },
    password: {
      required: helpers.withMessage('Enter your password', required),
      $autoDirty: true
    }
  },
  { username, password }
)

const open = () => {
  modalRef.value?.open()
}

const close = () => {
  modalRef.value?.close()
}

const resetForm = async () => {
  await sleep(200)
  username.value = ''
  password.value = ''
  errorMessage.value = ''
  $v.value.$reset()
}

const loginUser = async () => {
  const isValid = await $v.value.$validate()
  if (!isValid) return
  try {
    isLoading.value = true
    const { access_token, user } = await AuthService.login({
      username: username.value,
      password: password.value
    })
    setCookie('access_token', access_token, 1)
    setUser(user)
    errorMessage.value = ''
    await nextTick()
    close()
    await sleep(200)
    push('/')
  } catch (error: ApiError) {
    errorMessage.value = error.data?.message || 'An error occurred while logging in.'
  } finally {
    isLoading.value = false
  }
}

const openResetPassword = () => {
  close()
  setTimeout(() => {
    emit('open-reset-password')
  }, 100)
}

defineExpose({
  open,
  close
})
</script>
